import { useMemo } from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { clsx } from "clsx";
import { useExpenses } from "../../../lib/expenses/useExpenses";
import { useInvoices } from "../../../lib/invoices/useInvoices";
import {
  Card,
  EmptyState,
  formatCents,
  withinRange,
  computeRange,
  type RangePreset,
} from "./shared";

// CashFlow — headline tile showing money in / out / net for the selected
// range. Invoice direction semantics (matches InvoicesHub + InvoicesSummary):
//   outgoing = invoice you sent to a customer → receivable → money coming IN
//   incoming = invoice a vendor sent to you  → payable   → money going OUT
// Expenses are always outflows, so they stack into money out.
export function CashFlow({
  preset,
  submittedBy,
}: {
  preset: RangePreset;
  submittedBy?: string | null;
}) {
  const { invoices } = useInvoices();
  const { expenses } = useExpenses();

  const stats = useMemo(() => {
    const { startIso, endIso } = computeRange(preset);
    let revenueIn = 0;
    let invoiceOut = 0;
    let outgoingCount = 0;
    let incomingCount = 0;
    for (const inv of invoices) {
      if (submittedBy && inv.submittedBy !== submittedBy) continue;
      if (!withinRange(inv.issuedOn, startIso, endIso)) continue;
      if (inv.direction === "outgoing") {
        revenueIn += inv.amountCents;
        outgoingCount += 1;
      } else {
        invoiceOut += inv.amountCents;
        incomingCount += 1;
      }
    }
    let expenseOut = 0;
    let expenseCount = 0;
    for (const e of expenses) {
      if (submittedBy && e.submittedBy !== submittedBy) continue;
      if (!withinRange(e.occurredOn, startIso, endIso)) continue;
      expenseOut += e.amountCents;
      expenseCount += 1;
    }
    const moneyOut = invoiceOut + expenseOut;
    const net = revenueIn - moneyOut;
    return {
      revenueIn,
      moneyOut,
      net,
      outgoingCount,
      incomingCount,
      expenseCount,
    };
  }, [invoices, expenses, preset, submittedBy]);

  const isEmpty =
    stats.incomingCount === 0 &&
    stats.outgoingCount === 0 &&
    stats.expenseCount === 0;

  const netPositive = stats.net >= 0;

  return (
    <Card title="Cash flow" testId="report-cash-flow">
      {isEmpty ? (
        <EmptyState message="No invoices or expenses in this window yet." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div
            data-testid="report-cashflow-in"
            className="rounded-md border border-emerald-100 bg-emerald-50 p-3"
          >
            <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">
              Money in
            </div>
            <div className="text-2xl font-semibold text-emerald-700 mt-0.5 tabular-nums">
              {formatCents(stats.revenueIn)}
            </div>
            <div className="text-[11px] text-emerald-700/70 mt-0.5">
              from {stats.outgoingCount}{" "}
              {stats.outgoingCount === 1 ? "invoice" : "invoices"}
            </div>
          </div>

          <div
            data-testid="report-cashflow-out"
            className="rounded-md border border-rose-100 bg-rose-50 p-3"
          >
            <div className="text-[11px] uppercase tracking-wide text-rose-700 font-semibold">
              Money out
            </div>
            <div className="text-2xl font-semibold text-rose-700 mt-0.5 tabular-nums">
              {formatCents(stats.moneyOut)}
            </div>
            <div className="text-[11px] text-rose-700/70 mt-0.5">
              {stats.incomingCount}{" "}
              {stats.incomingCount === 1 ? "invoice" : "invoices"} +{" "}
              {stats.expenseCount}{" "}
              {stats.expenseCount === 1 ? "expense" : "expenses"}
            </div>
          </div>

          <div
            data-testid="report-cashflow-net"
            className="rounded-md border border-slate-200 bg-slate-50 p-3"
          >
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
              Net
            </div>
            <div
              className={clsx(
                "text-2xl font-semibold mt-0.5 tabular-nums flex items-center gap-1",
                netPositive ? "text-emerald-700" : "text-rose-700",
              )}
            >
              {netPositive ? (
                <ArrowUpRight className="w-5 h-5" aria-hidden="true" />
              ) : (
                <ArrowDownRight className="w-5 h-5" aria-hidden="true" />
              )}
              <span>{formatCents(stats.net)}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {netPositive ? "Positive cash flow" : "Negative cash flow"}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
